import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ls from 'local-storage';

import {
  IIPENNotaryNewRegistrationObj,
  IIPENNotaryProfile,
} from '../../../types/Notary';
import { ISearchParams } from '../../type';
import { calcDaysUntilExpiry, isFANotary } from '../../utils/helpers';
import { selectConfig } from '../global/globalSlice';
import { RootState } from '../store';
import {
  confirmNotaryRequest,
  getAllNotaries,
  getNotaryByEmail,
  getNotarySealTemplate,
  getSsoToken,
  getUserLoginDetails,
  resendRegistrationEmail,
  searchNotaries,
  sendRegistrationEmail,
  submitNotary,
  updateNotary,
  updateNotaryStatus,
} from './ipenNotaryAPI';
import {
  IIPENNotaryState,
  IIPENNotaryStatusUpdate,
  ISsoTokenRequest,
} from './ipenNotaryTypes';

export const initialState: IIPENNotaryState = {
  alertUser: null,
  daysUntilExpiry: null,
  errors: undefined,
  isLoading: false,
  isFetching: false,
  loadingNotaries: false,
  notaries: [],
  notariesPaginationToken: undefined,
  notary: undefined,
  isSending: false,
  isFANotary: false,
  hasLoggedIn: null,
};

export const confirmNotaryRequestAsync = createAsyncThunk(
  'ipen/updateNotary',
  async (orderId: string, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      return await confirmNotaryRequest(orderId, config);
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const getAllNotariesAsync = createAsyncThunk(
  'getAllNotaries',
  async (_, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      return await getAllNotaries(config);
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const getNotaryByEmailAsync = createAsyncThunk(
  'getNotaryByEmail',
  async (email: any, { dispatch, getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      const response = await getNotaryByEmail(email, config);
      dispatch(setIsFANotary(email));
      return response;
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const getSsoTokenAsync = createAsyncThunk(
  'ipen/ssoToken',
  async (values: ISsoTokenRequest, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      return await getSsoToken(values, config);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUserLoginDetailsAsync = createAsyncThunk(
  'ipen/userLoginDetails',
  async (params: { email: string | null }, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    let response = { HasLoggedIn: false };
    try {
      if (params.email) {
        response = await getUserLoginDetails(params.email, config);
      }
      return response;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const resendRegistrationEmailAsync = createAsyncThunk(
  'ipen/resendRegistrationEmail',
  async (emails: string[], { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      return await resendRegistrationEmail(emails, config);
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const searchNotariesAsync = createAsyncThunk(
  'ipen/searchNotaries',
  async (
    data: {
      parameters: ISearchParams;
      loadMore?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    const { parameters, loadMore } = data;
    const config: any = selectConfig(getState() as any);
    try {
      const response = await searchNotaries(parameters, config);
      if (loadMore) {
        selectNotaries(getState() as any);
      }

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const loadMoreNotariesAsync = createAsyncThunk(
  'ipen/loadMoreNotariesAsync',
  async (
    data: {
      parameters: ISearchParams;
    },
    { getState, rejectWithValue }
  ) => {
    const { parameters } = data;
    const config: any = selectConfig(getState() as any);
    // don't need custom pagination for dropdown
    parameters.direction = 'skip';
    try {
      return await searchNotaries(parameters, config);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitNotaryAsync = createAsyncThunk(
  'submitNotary',
  async (notary: any, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      return await submitNotary(notary, config);
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const submitRegistrationEmailsAsync = createAsyncThunk(
  'ipenNotary/sendRegistrationEmail',
  async (
    data: IIPENNotaryNewRegistrationObj,
    { getState, rejectWithValue }
  ) => {
    const config: any = selectConfig(getState() as any);
    try {
      return await sendRegistrationEmail(data, config);
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const updateNotaryAsync = createAsyncThunk(
  'updateNotaryProfile',
  async (notary: any, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      return await updateNotary(notary, config);
    } catch (err: string | unknown) {
      return rejectWithValue(err);
    }
  }
);

export const updateNotaryStatusAsync = createAsyncThunk(
  'ipen/updateStatus',
  async (values: IIPENNotaryStatusUpdate, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      return await updateNotaryStatus(values, config);
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const getNotarySealTemplateAsync = createAsyncThunk(
  'ipen/fetchNotarySealTemplate',
  async (stateAbbreviation: string, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    try {
      return await getNotarySealTemplate(stateAbbreviation, config);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const ipenNotarySlice = createSlice({
  name: 'ipen-notary',
  initialState,
  reducers: {
    setNotary(state: any, action: PayloadAction<IIPENNotaryProfile>) {
      state.notary = action.payload;
    },
    setAlert: (state: any, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.alertUser = action.payload
        ? action.payload.type
          ? { type: action.payload.type, text: action.payload.text }
          : { type: 'info', text: action.payload }
        : null;
    },
    setDaysUntilExpiry: (state: any, action: PayloadAction<any>) => {
      state.daysUntilExpiry = calcDaysUntilExpiry(action.payload);
    },
    setIsFANotary: (state: any, action: PayloadAction<any>) => {
      state.isFANotary = isFANotary(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      //getUserLoginDetailsAsync
      .addCase(getUserLoginDetailsAsync.pending, (state: any) => {
        state.isLoading = true;
        state.alertUser = null;
      })
      .addCase(
        getUserLoginDetailsAsync.fulfilled,
        (state: any, action: any) => {
          state.isLoading = false;
          state.alertUser = { type: 'info', text: 'Success!' };
          state.hasLoggedIn = action.payload.HasLoggedIn;
        }
      )
      .addCase(getUserLoginDetailsAsync.rejected, (state: any, action: any) => {
        const errorMessage = () => {
          if (action.payload?.FaultedMembers) {
            if (action.payload?.Message) {
              return (
                action.payload.FaultedMembers + ': ' + action.payload.Message
              );
            } else {
              return action.payload.FaultedMembers;
            }
          } else {
            return 'An error occurred. Please try again.';
          }
        };
        state.isLoading = false;
        state.alertUser = {
          type: 'error',
          text: errorMessage(),
        };
      })
      // SUBMIT NOTARY REG EMAILS
      .addCase(submitRegistrationEmailsAsync.pending, (state) => {
        state.isSending = true;
      })
      .addCase(submitRegistrationEmailsAsync.fulfilled, (state: any) => {
        state.isSending = false;
      })
      .addCase(submitRegistrationEmailsAsync.rejected, (state: any) => {
        state.isSending = false;
      })
      // SUBMIT NOTARY PROFILE
      .addCase(submitNotaryAsync.pending, (state) => {
        state.isLoading = true;
        state.alertUser = null;
      })
      .addCase(submitNotaryAsync.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.order = action.payload.order;
        state.notary = action.payload.notary;
        state.daysUntilExpiry = calcDaysUntilExpiry(
          action.payload.notary?.CommissionExpirationDate
        );
        state.alertUser = { type: 'success', text: action.payload };
      })
      .addCase(submitNotaryAsync.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.alertUser = { type: 'error', text: action.payload };
      })
      // GET NOTARY BY EMAIL
      .addCase(getNotaryByEmailAsync.pending, (state: any) => {
        state.isLoading = true;
        state.alertUser = null;
      })
      .addCase(getNotaryByEmailAsync.fulfilled, (state: any, action: any) => {
        state.notary = action.payload;
        state.isLoading = false;
        state.daysUntilExpiry = calcDaysUntilExpiry(
          action.payload?.CommissionExpirationDate
        );
        ls(
          'CommissionExpirationDate',
          action.payload?.CommissionExpirationDate === undefined
            ? null
            : action.payload.CommissionExpirationDate
        );
        ls('CommissionStatus', action.payload?.Status);
        ls('NotaryBranchId', action.payload?.BranchId);
      })
      .addCase(getNotaryByEmailAsync.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.alertUser = { type: 'error', text: action.payload };
      })
      // GET ALL NOTARIES
      .addCase(getAllNotariesAsync.pending, (state: any) => {
        state.isLoading = true;
        state.alertUser = null;
        state.notariesPaginationToken = undefined;
      })
      .addCase(getAllNotariesAsync.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.notaries = action.payload.Notaries;
        state.notariesPaginationToken = action.payload.PaginationToken;
      })
      .addCase(getAllNotariesAsync.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.alertUser = { type: 'error', text: action.payload };
        state.notariesPaginationToken = undefined;
      })
      // RESEND NOTARY REGISTRATION LINK
      .addCase(resendRegistrationEmailAsync.pending, (state: any) => {
        state.isLoading = true;
        state.isReSending = true;
      })
      .addCase(
        resendRegistrationEmailAsync.fulfilled,
        (state: any, action: any) => {
          state.isLoading = false;
          state.isReSending = false;
          state.alertUser = { type: 'success', text: action.payload.message };
        }
      )
      .addCase(
        resendRegistrationEmailAsync.rejected,
        (state: any, action: any) => {
          state.isLoading = false;
          state.isReSending = false;
          state.alertUser = { type: 'error', text: action.payload };
        }
      )
      // UPDATE NOTARY STATUS
      .addCase(updateNotaryStatusAsync.pending, (state: any) => {
        state.isLoading = true;
        state.alertUser = null;
      })
      .addCase(updateNotaryStatusAsync.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.alertUser = { type: 'success', text: action.payload.message };
      })
      .addCase(updateNotaryStatusAsync.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.alertUser = { type: 'error', text: action.payload.message };
      })
      // Update Notary Request
      .addCase(confirmNotaryRequestAsync.pending, (state: any) => {
        state.isSending = true;
        state.alertUser = null;
      })
      .addCase(
        confirmNotaryRequestAsync.fulfilled,
        (state: any, action: any) => {
          state.isSending = false;
          state.alertUser = { type: 'success', text: action.payload.message };
        }
      )
      .addCase(
        confirmNotaryRequestAsync.rejected,
        (state: any, action: any) => {
          state.isSending = false;
          state.alertUser = { type: 'error', text: action.payload.message };
        }
      )
      // Get Notary Token
      .addCase(getSsoTokenAsync.pending, (state: any) => {
        state.isLoading = true;
        state.isFetching = true;
      })
      .addCase(getSsoTokenAsync.fulfilled, (state: any) => {
        state.isLoading = false;
        state.isFetching = false;
      })
      .addCase(getSsoTokenAsync.rejected, (state: any, action: any) => {
        state.isLoading = false;
        state.isFetching = false;
        state.alertUser = { type: 'error', text: action.payload.message };
      })
      // Search Notaries
      .addCase(searchNotariesAsync.pending, (state: any) => {
        state.isLoading = true;
        state.alertUser = null;
        state.notaries = [];
        state.notariesPaginationToken = undefined;
      })
      .addCase(searchNotariesAsync.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.notaries = action.payload.Notaries;
        state.notariesPaginationToken = action.payload.PaginationToken;
      })
      .addCase(searchNotariesAsync.rejected, (state: any, action: any) => {
        state.notaries = [];
        state.notariesPaginationToken = undefined;
        state.isLoading = false;
        state.alertUser = { type: 'error', text: action.payload };
      })
      // Load More Notaries
      .addCase(loadMoreNotariesAsync.pending, (state: any) => {
        state.isLoading = true;
        state.alertUser = null;
        state.notariesPaginationToken = undefined;
      })
      .addCase(loadMoreNotariesAsync.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.notaries = state.notaries.concat(action.payload.Notaries);
        state.notariesPaginationToken = action.payload.PaginationToken;
      })
      .addCase(loadMoreNotariesAsync.rejected, (state: any, action: any) => {
        state.notaries = [];
        state.notariesPaginationToken = undefined;
        state.isLoading = false;
        state.alertUser = { type: 'error', text: action.payload };
      })
      // Update Notary Profile
      .addCase(updateNotaryAsync.fulfilled, (state: any) => {
        state.isLoading = false;
      })
      .addCase(updateNotaryAsync.pending, (state: any) => {
        state.isLoading = true;
      })
      .addCase(updateNotaryAsync.rejected, (state: any) => {
        state.isLoading = true;
      });
  },
});

export const { setAlert, setNotary, setDaysUntilExpiry, setIsFANotary } =
  ipenNotarySlice.actions;
export const selectNotaries = (state: RootState) => state.ipenNotary.notaries;
export default ipenNotarySlice.reducer;
